import React, { FC, memo, useState, useRef, CSSProperties, useLayoutEffect } from 'react';
import { DynamicErrorHandler } from '../dynamic-error-handler';
import { IImage } from '@montana/models/generated/cms.interface';
import { convertMediaUrl } from '@montana/helpers/media-src.helper';
import { VideoCurtain } from './video-curtain/video-curtain.component';
import { isBrowser } from '@montana/helpers/browser.helper';
import cx from 'clsx';
import { IYoutubePlayerRef } from './youtube-player';
import { SvgIcon } from '../svg-icon';
import classNames from 'classnames';
import { noop } from 'lodash-es';

export interface IVideoProps {
    url: string;
    title?: string;
    thumbnail?: IImage;
    autoplay?: boolean;
    loop?: boolean;
    playInline?: boolean;
    muted?: boolean;
    onReady?: () => void;
    style?: CSSProperties;
}

/**
 * Get YouTube ID from most variations of YouTube URLs
 *
 * @param {string} url - YouTube URL
 * @returns {string} The ID or and empty string if no ID was found.
 */
const getYoutubeIdFromUrl = (url: string): string => {
    // eslint-disable-next-line no-useless-escape
    const urlRegex = /(?:youtube(?:-nocookie)?\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/i;
    const match = url.match(urlRegex);
    return match && match.length > 1 ? match[1] : '';
}

const LazyYoutubePlayer = React.lazy(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (): Promise<{ default: React.ComponentType<any> }> => import(/* webpackChunkName: "youtube-player" */"./youtube-player"),
);

export const Video: FC<IVideoProps> = memo(({ thumbnail, autoplay = true, muted = false, loop = true, playInline = true, url, onReady, style, title }: IVideoProps): JSX.Element => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(autoplay || muted);
    const [userToggledSound, setUserToggledSound] = useState(false);
    const [disableThumbnail, setDisableThumbnail] = useState(autoplay);
    const [isReady, setIsReady] = useState(false);
    const youTubeId = getYoutubeIdFromUrl(url);

    const videoPlayerRef = useRef<HTMLVideoElement | IYoutubePlayerRef>(null);

    loop = loop || autoplay;
    playInline = autoplay || playInline;

    const RenderYoutube = (
        <DynamicErrorHandler kind={`LazyYoutubeLoader`}>
            {/* It may seem weird to use "key" here, but it forces React to re-render the component with potential new video-data. */}
            <React.Suspense key={youTubeId} fallback={<div className="video__container" style={style}>[Failed to load video]</div>}>
                <LazyYoutubePlayer
                    autoPlay={autoplay}
                    muted={isMuted}
                    loop={loop}
                    playInline={playInline}
                    onStateChange={setIsPlaying}
                    ref={videoPlayerRef as any}
                    thumbnail={thumbnail}
                    youtubeVideoId={youTubeId}
                    onReady={onReady}
                />
            </React.Suspense>
        </DynamicErrorHandler>
    );

    const toggleVideo = () => {
        if (isPlaying) {
            videoPlayerRef.current?.pause();
        } else {
            videoPlayerRef.current?.play();
        }

        setIsPlaying(p => !p);
    }

    const play = () => {
        videoPlayerRef.current?.play();
        setDisableThumbnail(true);
    }

    function toggleSound(): void {
        setIsMuted((m) => !m);

        if (!userToggledSound) {
            setUserToggledSound(true);
        }
    }

    const getThumbnail = (): IImage | undefined => {
        if (thumbnail) {
            return thumbnail;
        }
        if (youTubeId) {
            return {
                url: `https://img.youtube.com/vi/${youTubeId}/maxresdefault.jpg`
            };
        }

        return undefined;
    };

    // Video events don't seem to work when building production app... Fire ready on component load to make sure to fade video in.
    (isBrowser ? useLayoutEffect : noop)(() => {
        fireOnReadyCallback();
    }, []);

    const fireOnReadyCallback = () => {
        if (!isReady && onReady) {
            setIsReady(true);
            onReady();
        }
    }

    return (
        <div className={cx('video__container', { 'video__container--ready': isReady })} style={style}>
            {youTubeId && isBrowser ?
                RenderYoutube :
                <video
                    ref={videoPlayerRef as any}
                    title={title}
                    autoPlay={autoplay}
                    muted={isMuted}
                    loop={loop}
                    onClick={toggleVideo}
                    playsInline={playInline}
                    onPlay={() => setIsPlaying(true)}
                    onPlaying={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => setIsPlaying(false)}
                >
                    <source src={convertMediaUrl(url)} type="video/mp4" />
                </video>
            }
            {autoplay && !muted &&
                <button aria-label="toggle sound" className={classNames("video__toggle-sound", userToggledSound && "video__toggle-sound--show-on-hover")} onClick={toggleSound}><SvgIcon iconName={isMuted ? "volume-up" : "volume-mute"} /></button>
            }
            {!disableThumbnail && <VideoCurtain onClick={play} showIcon={!autoplay} thumbnail={getThumbnail()} />}
        </div>
    )
});
